/** Webhook endpoint manager — Phase 3-pp-webhook.
 *
 * Org admins use this to register URLs that receive HMAC-signed event
 * POSTs. The signing secret is shown ONCE on creation + rotate.
 * Test-fire sends a `webhook.test` event to a single endpoint so
 * customers can verify their receiver before going live. Delivery
 * log below the endpoint list surfaces last-100 events with status,
 * attempts, and last response.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatTime } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type {
  Paginated,
  WebhookEndpoint,
  WebhookEndpointIssued,
  WebhookEvent,
} from "@/lib/types";

const EVENT_TYPES: string[] = [
  "*",
  "session.created",
  "session.updated",
  "session.completed",
  "session.cancelled",
  "participant.joined",
  "participant.left",
  "evidence.added",
  "recording.ready",
  "audit.anchored",
  "signature.completed",
];

export function WebhooksCard() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [eventTypes, setEventTypes] = useState<Set<string>>(new Set(["*"]));
  const [revealed, setRevealed] = useState<WebhookEndpointIssued | null>(null);

  const list = useQuery({
    queryKey: ["webhook-endpoints"],
    queryFn: async () => {
      const res = await api.get<WebhookEndpoint[]>(
        ApiPaths.orgWebhookEndpoints,
      );
      return res.data;
    },
  });

  const events = useQuery({
    queryKey: ["webhook-events"],
    queryFn: async () => {
      // Recent delivery log — request a bounded page (preserves the prior
      // 100-row cap); the endpoint is paginated server-side.
      const res = await api.get<Paginated<WebhookEvent>>(
        ApiPaths.orgWebhookEvents,
        { params: { page_size: "100" } },
      );
      return res.data.results ?? [];
    },
    refetchInterval: 15_000,
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<WebhookEndpointIssued>(
        ApiPaths.orgWebhookEndpoints,
        {
          url: url.trim(),
          description: description.trim(),
          event_types: Array.from(eventTypes),
        },
      );
      return res.data;
    },
    onSuccess: (issued) => {
      setRevealed(issued);
      setUrl("");
      setDescription("");
      setEventTypes(new Set(["*"]));
      qc.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });

  const rotate = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<WebhookEndpointIssued>(
        ApiPaths.orgWebhookRotateSecret(id),
      );
      return res.data;
    },
    onSuccess: (issued) => {
      setRevealed(issued);
      qc.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });

  const testFire = useMutation({
    mutationFn: async (id: string) => {
      await api.post(ApiPaths.orgWebhookTestFire(id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-events"] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ApiPaths.orgWebhookEndpoint(id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-endpoints"] }),
  });

  const toggleEvent = (slug: string) =>
    setEventTypes((prev) => {
      const next = new Set(prev);
      if (slug === "*") {
        return new Set(["*"]);
      }
      next.delete("*");
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      if (next.size === 0) next.add("*");
      return next;
    });

  const canCreate = url.trim().startsWith("https://");

  return (
    <section className="card space-y-4" data-testid="webhooks-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.webhooks.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.webhooks.cardHint")}</p>
      </header>

      {revealed && (
        <div
          className="space-y-2 rounded-md border border-cvp-warn bg-cvp-warn/10 px-3 py-2 text-xs text-amber-100"
          role="alert"
          data-testid="webhook-secret-reveal"
        >
          <p className="font-semibold">{t("admin.webhooks.copySecretNow")}</p>
          <code className="block break-all rounded bg-black/40 px-2 py-1 text-amber-200">
            {revealed.signing_secret}
          </code>
          <p className="text-nb-text">{revealed.url}</p>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setRevealed(null)}
          >
            {t("admin.apiKeys.iStoredIt")}
          </button>
        </div>
      )}

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canCreate) create.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="wh-url">{t("admin.webhooks.deliveryUrl")}</label>
          <input
            id="wh-url"
            className="input"
            value={url}
            placeholder="https://customer.example.com/nb-webhook"
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="wh-desc">{t("admin.webhooks.description")}</label>
          <input
            id="wh-desc"
            className="input"
            value={description}
            placeholder="Acme — production"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label className="label">{t("admin.webhooks.eventTypes")}</label>
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
            {EVENT_TYPES.map((slug) => (
              <label
                key={slug}
                className="flex items-center gap-2 rounded-md border border-nb-border bg-slate-900/40 px-2 py-1 text-xs text-nb-text"
              >
                <input
                  type="checkbox"
                  checked={eventTypes.has(slug)}
                  onChange={() => toggleEvent(slug)}
                />
                <code>{slug}</code>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-nb-text-3">{t("admin.webhooks.wildcardHint")}</p>
        </div>
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={!canCreate || create.isPending}
        >
          {create.isPending ? t("admin.webhooks.creating") : t("admin.webhooks.createButton")}
        </button>
      </form>

      <div className="space-y-2 text-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.webhooks.registeredEndpoints")}
        </h3>
        {list.isLoading && <p className="text-nb-text-2">{t("common.loading")}</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-nb-text-2">{t("admin.webhooks.noEndpoints")}</p>
        )}
        <ul className="space-y-2">
          {(list.data ?? []).map((e) => (
            <li
              key={e.id}
              className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <code className="break-all text-nb-text">{e.url}</code>
                <span
                  className={`rounded px-2 py-0.5 ${
                    e.enabled
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-cvp-danger/20 text-cvp-danger"
                  }`}
                >
                  {e.enabled ? t("admin.webhooks.enabled") : t("admin.webhooks.disabled")}
                </span>
              </div>
              {e.description && (
                <div className="mt-0.5 text-nb-text-2">{e.description}</div>
              )}
              <div className="mt-1 flex flex-wrap gap-1">
                {e.event_types.map((s) => (
                  <code key={s} className="rounded bg-nb-surface px-1 text-nb-text">
                    {s}
                  </code>
                ))}
              </div>
              <div className="mt-1 text-nb-text-3">
                <code>whsec_{e.signing_secret_prefix}…</code>
                {e.consecutive_failures > 0 && (
                  <span className="ml-2 text-cvp-warn">
                    · {t("admin.webhooks.consecutiveFailures", { count: e.consecutive_failures })}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => testFire.mutate(e.id)}
                  disabled={testFire.isPending}
                >
                  {t("admin.webhooks.testFire")}
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => {
                    if (window.confirm(t("admin.webhooks.rotateConfirm"))) {
                      rotate.mutate(e.id);
                    }
                  }}
                  disabled={rotate.isPending}
                >
                  {t("admin.webhooks.rotateSecret")}
                </button>
                <button
                  type="button"
                  className="btn-danger text-xs"
                  onClick={() => {
                    if (
                      window.confirm(
                        t("admin.webhooks.deleteConfirm", { url: e.url }),
                      )
                    ) {
                      remove.mutate(e.id);
                    }
                  }}
                  disabled={remove.isPending}
                >
                  {t("common.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-2 text-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.webhooks.recentDeliveries")}
        </h3>
        {events.data && events.data.length === 0 && (
          <p className="text-nb-text-2">{t("admin.webhooks.noDeliveries")}</p>
        )}
        <ul className="space-y-1">
          {(events.data ?? []).slice(0, 20).map((ev) => (
            <li
              key={ev.id}
              className="rounded-md border border-nb-border bg-slate-900/40 px-2 py-1 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <code className="text-nb-text">{ev.event_type}</code>
                <span
                  className={`rounded px-2 py-0.5 ${
                    ev.status === "delivered"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : ev.status === "failed"
                        ? "bg-cvp-warn/20 text-amber-200"
                        : ev.status === "dropped"
                          ? "bg-cvp-danger/20 text-cvp-danger"
                          : "bg-slate-700 text-nb-text"
                  }`}
                >
                  {ev.status}
                </span>
              </div>
              <div className="text-nb-text-3">
                attempts {ev.attempts}
                {ev.last_response_status &&
                  ` · HTTP ${ev.last_response_status}`}
                {ev.delivered_at &&
                  ` · delivered ${formatTime(ev.delivered_at)}`}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
